// src/components/tip/WalletDashboardInner.jsx

import React, { useContext, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  TextField,
  useMediaQuery,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link as MuiLink,
  Tooltip,
  TableContainer
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@apollo/client';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { parseISO, isWithinInterval, format } from 'date-fns';

import {
  GET_WALLET_SUMMARY,
  GET_COIN_TRANSACTIONS,
} from '../../graphql/queries/tip';
import { UserContext } from '../auth/auth-context';
import PaymentSettings from '../../pages/customer/booking/paymentSettings';
import { useCoinConfig } from './tipProvider';


const tableContainerSx = {
  maxHeight: 220,                 // ≈ 4 rows × 20 px
  overflowY: 'auto',
  scrollbarWidth: 'thin',         // Firefox
  '&::-webkit-scrollbar': {       // Chrome / Edge
    width: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#c0c0c0',
    borderRadius: '4px',
  },
};

// Helper: parse ISO string or numeric timestamp
const parseDate = (val) =>
  typeof val === 'string' && /^\d+$/.test(val)
    ? new Date(Number(val))
    : parseISO(val);

export default function WalletDashboardInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { businessUrl, staffUrl } = useParams();
  const { providerUser } = useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { minWithdrawal } = useCoinConfig();
  // IDs for queries & permissions
  const staffId = providerUser?.staffId;
  const businessId = providerUser?.defaultBusinessId;
  const canViewBusiness = Boolean(businessId);
  const isCreator = providerUser?.url === staffUrl;

  // View toggle state
  const [view, setView] = useState('staff');

  // Date filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showCalc, setShowCalc] = useState(false);

  // Separate copy-feedback states
  const [copiedStaff, setCopiedStaff] = useState(false);
  const [copiedBusiness, setCopiedBusiness] = useState(false);

  // Menu & settings dialog state
  const [anchorEl, setAnchorEl] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const menuOpen = Boolean(anchorEl);

  // Redirect unauthenticated or wrong user to their own wallet
  useEffect(() => {
    if (!providerUser?.staffId) {
      navigate('/login');
    } else if (!isCreator) {
      navigate(`/${businessUrl}/${providerUser.url}/wallet`);
    }
  }, [providerUser, isCreator, businessUrl, navigate]);

  // After a withdraw, refetch data
  useEffect(() => {
    if (location.state?.didWithdraw || location.state?.didPurchase) {
      refetchSummary();
      refetchTx();
      setSettingsOpen(false);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Fetch wallet summary
  const {
    data: sumData,
    loading: sumLoading,
    refetch: refetchSummary,
  } = useQuery(GET_WALLET_SUMMARY, {
    variables: {
      staffId: view === 'staff' ? staffId : null,
      businessId: view === 'business' ? businessId : null,
    },
    skip:
      (view === 'staff' && !staffId) ||
      (view === 'business' && !businessId),
  });

  // Fetch coin transactions
  const {
    data: txData,
    loading: txLoading,
    refetch: refetchTx,
  } = useQuery(GET_COIN_TRANSACTIONS, {
    variables: {
      staffId: view === 'staff' ? staffId : null,
      businessId: view === 'business' ? businessId : null,
    },
    skip:
      (view === 'staff' && !staffId) ||
      (view === 'business' && !businessId),
  });


  // Loading & auth guard
  if (!providerUser?.staffId || !isCreator) return null;
  if (sumLoading || txLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Destructure summary & transactions
  const summary = sumData?.getWalletSummary || {};
  const transactions = txData?.getPlanyCoinTransactions || [];

  // Date filter helper
  const filterByDate = (list) =>
    !startDate || !endDate
      ? list
      : list.filter((t) => {
        const d = parseDate(t.createdAt);
        return isWithinInterval(d, {
          start: parseDate(startDate),
          end: parseDate(endDate),
        });
      });

  // Partition transactions
  const purchases = filterByDate(transactions.filter(t => t.type === 'purchase'));
  const cashOuts = filterByDate(transactions.filter(t => t.type === 'withdraw'));
  const receivedTxStaff = filterByDate(
    transactions.filter(
      t => t.type === 'tip' && Number(t.targetStaff?.id) === Number(staffId)
    )
  );
  const sentTxStaff = filterByDate(
    transactions.filter(
      t => t.type === 'tip' && Number(t.staff?.id) === Number(staffId)
    )
  );
  const bizReceived = filterByDate(
    transactions.filter(
      t => t.type === 'tip' && Number(t.targetBusiness?.id) === Number(businessId)
    )
  );
  const bizCashOut = cashOuts.filter(
    t => Number(t.targetBusiness?.id) === Number(businessId)
  );

  // Tip page URLs
  const tipPageUrlStaff = `/${businessUrl}/${staffUrl}/tip`;
  const tipPageUrlBusiness = `/${businessUrl}/tip`;
  const fullTipUrlStaff = `${window.location.origin}${tipPageUrlStaff}`;
  const fullTipUrlBusiness = `${window.location.origin}${tipPageUrlBusiness}`;

  // Copy handlers
  const copyStaffUrl = () => {
    navigator.clipboard
      .writeText(fullTipUrlStaff)
      .then(() => {
        setCopiedStaff(true);
        setTimeout(() => setCopiedStaff(false), 2000);
      });
  };
  const copyBusinessUrl = () => {
    navigator.clipboard
      .writeText(fullTipUrlBusiness)
      .then(() => {
        setCopiedBusiness(true);
        setTimeout(() => setCopiedBusiness(false), 2000);
      });
  };

  // Menu handlers
  const handleMenuOpen = e => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleSettingsOpen = () => {
    setSettingsOpen(true);
    handleMenuClose();
  };
  const handleSettingsClose = () => setSettingsOpen(false);

  // Table layout styling
  const colWidths = { date: '80px', user: '100px', coins: '60px' };
  const tableSx = {
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    width: 'auto',
    '& th, & td': { padding: '6px', whiteSpace: 'nowrap' },
  };

  return (
    <Box p={isMobile ? 2 : 3}>
      {/* View Toggle */}
      <Box mb={2} display="flex" gap={1}>
        <Button
          variant={view === 'staff' ? 'contained' : 'outlined'}
          onClick={() => setView('staff')}
        >
          My Wallet
        </Button>
        {canViewBusiness && (
          <Button
            variant={view === 'business' ? 'contained' : 'outlined'}
            onClick={() => setView('business')}
          >
            Business Wallet
          </Button>
        )}
      </Box>

      {/* Header & Menu */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">
          {view === 'staff' ? '🪙 Your PlanyCoin Wallet' : '🏢 Business Wallet'}
        </Typography>
        <IconButton onClick={handleMenuOpen} size="large">
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={handleSettingsOpen}>PayPal Settings</MenuItem>
          {view === 'staff' && (
            <MenuItem
              onClick={() => {
                navigate('/coins/buy');
                handleMenuClose();
              }}
            >
              Buy Coins
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              navigate(`/${businessUrl}/${staffUrl}/withdraw`);
              handleMenuClose();
            }}
            disabled={summary.balance < minWithdrawal}
          >
            Cash Out to PayPal
          </MenuItem>
        </Menu>
      </Box>

      {/* Summary Panel */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography variant="h6">
              Balance: {summary.balance} PlanyCoins
            </Typography>
            {view === 'staff' && (
              <Button size="small" onClick={() => setShowCalc(!showCalc)} sx={{ mt: 1 }}>
                {showCalc ? 'Hide Calculation' : 'Show Calculation'}
              </Button>
            )}
            {showCalc && view === 'staff' && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                Purchased ({summary.totalPurchased}) + Received ({summary.tipsReceived}) – Sent ({summary.tipsSent}) – Cash Out ({summary.totalSpent})
              </Typography>
            )}
          </Box>

          <Box mt={isMobile ? 2 : 0}>
            {view === 'staff' && (
              <Button
                variant="contained"
                color="secondary"
                component={Link}
                to="/coins/buy"
                sx={{ mr: isMobile ? 0 : 2 }}
              >
                Buy Coins
              </Button>
            )}
            <Tooltip
              title={
                summary.balance < minWithdrawal
                  ? `Minimum withdraw is ${minWithdrawal} coins`
                  : ''
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={summary.balance < minWithdrawal}
                  onClick={() =>
                    navigate(`/${businessUrl}/${staffUrl}/withdraw`)
                  }
                >
                  Cash Out
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Tip Page URLs */}
        <Box mt={2}>
          <Typography fontWeight="bold">Personal Tip URL</Typography>
          <Box display="flex" alignItems="center" mb={1}>
            <MuiLink
              href={fullTipUrlStaff}
              sx={{ wordBreak: 'break-all', fontWeight: 500 }}
            >
              {fullTipUrlStaff}
            </MuiLink>
            <IconButton size="small" onClick={copyStaffUrl}>
              {copiedStaff ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
            </IconButton>
          </Box>

          {canViewBusiness && (
            <>  <Typography fontWeight="bold">Business Tip URL</Typography>
              <Box display="flex" alignItems="center">
                <MuiLink
                  href={fullTipUrlBusiness}
                  sx={{ wordBreak: 'break-all', fontWeight: 500 }}
                >
                  {fullTipUrlBusiness}
                </MuiLink>
                <IconButton size="small" onClick={copyBusinessUrl}>
                  {copiedBusiness ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                </IconButton>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      {/* Date Filter */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filter by Date
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            type="date"
            label="Start Date"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
          <TextField
            type="date"
            label="End Date"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
        </Box>
      </Paper>

      {/* Transactions */}
      {view === 'staff' ? (
        <>
          {/* Coins Purchased */}
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Coins Purchased
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer sx={tableContainerSx}>
              <Table size="small" stickyHeader sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: colWidths.date }}>Date</TableCell>
                    <TableCell sx={{ width: colWidths.coins }}>Coins</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchases.map(tx => (
                    <TableRow key={tx.id}>
                      <TableCell>{format(parseDate(tx.createdAt), 'yyyy-MM-dd')}</TableCell>
                      <TableCell>{tx.amount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Tips Received */}
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Tips Received
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer sx={tableContainerSx}>
              <Table size="small" stickyHeader sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: colWidths.date }}>Date</TableCell>
                    <TableCell sx={{ width: colWidths.user }}>From</TableCell>
                    <TableCell sx={{ width: colWidths.coins }}>Coins</TableCell>
                    <TableCell>Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receivedTxStaff.map(tx => (
                    <TableRow key={tx.id}>
                      <TableCell>{format(parseDate(tx.createdAt), 'yyyy-MM-dd')}</TableCell>
                      <TableCell>{tx.staff?.user?.username}</TableCell>
                      <TableCell>{tx.amount}</TableCell>
                      <TableCell>{tx.note}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Tips Sent */}
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Tips Sent
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer sx={tableContainerSx}>
              <Table size="small" stickyHeader sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Coins</TableCell>
                    <TableCell>Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sentTxStaff.map(tx => (
                    <TableRow key={tx.id}>
                      <TableCell>{format(parseDate(tx.createdAt), 'yyyy-MM-dd')}</TableCell>
                      <TableCell>{tx.targetStaff?.user?.username ?? tx.targetBusiness?.name}</TableCell>
                      <TableCell>{tx.amount}</TableCell>
                      <TableCell>{tx.note}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Cash Out */}
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Cash Out
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer sx={tableContainerSx}>
              <Table size="small" stickyHeader sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Coins</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cashOuts.map(tx => (
                    <TableRow key={tx.id}>
                      <TableCell>{format(parseDate(tx.createdAt), 'yyyy-MM-dd')}</TableCell>
                      <TableCell>{Math.abs(tx.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      ) : (
        <>
          {/* Business: Tips Received */}
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Tips Received
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer sx={tableContainerSx}>
              <Table size="small" stickyHeader sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>Coins</TableCell>
                    <TableCell>Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bizReceived.map(tx => (
                    <TableRow key={tx.id}>
                      <TableCell>{format(parseDate(tx.createdAt), 'yyyy-MM-dd')}</TableCell>
                      <TableCell>{tx.staff.user.username}</TableCell>
                      <TableCell>{tx.amount}</TableCell>
                      <TableCell>{tx.note}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Business: Cash Out */}
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Cash Out
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer sx={tableContainerSx}>
              <Table size="small" stickyHeader sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Coins</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bizCashOut.map(tx => (
                    <TableRow key={tx.id}>
                      <TableCell>{format(parseDate(tx.createdAt), 'yyyy-MM-dd')}</TableCell>
                      <TableCell>{Math.abs(tx.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={handleSettingsClose} fullWidth maxWidth="md">
        <DialogTitle>Business Integration Settings</DialogTitle>
        <DialogContent dividers>
          <PaymentSettings isDisabled={false} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSettingsClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
